/**
 * Structural callouts — Problem, Solution, Step, Result, Check, Pitfall.
 *
 * Worked problems are the part of a physics text that most needs visible
 * structure: you should be able to see at a glance where the problem statement
 * ends and the solution begins, and be able to find step 3 again tomorrow.
 * Prose alone doesn't give you that, so the tutor marks these blocks with
 * GitHub-style `> [!problem]` alert syntax and they are typeset as real
 * textbook furniture.
 */

import type { ReactNode } from "react";
import {
  Baseline,
  BadgeCheck,
  ClipboardList,
  CornerDownRight,
  Lightbulb,
  Sigma,
  TriangleAlert,
} from "lucide-react";

export const CALLOUT_KINDS = [
  "problem",
  "solution",
  "step",
  "result",
  "check",
  "pitfall",
  "idea",
  "symbols",
] as const;

export type CalloutKind = (typeof CALLOUT_KINDS)[number];

interface CalloutStyle {
  readonly label: string;
  readonly icon: typeof Sigma;
  /** Border, background, and label colour, keyed to the app's tokens. */
  readonly frame: string;
  readonly labelColor: string;
}

const STYLES: Readonly<Record<CalloutKind, CalloutStyle>> = {
  problem: {
    label: "Problem",
    icon: ClipboardList,
    frame: "border-primary/40 bg-brand-soft",
    labelColor: "text-primary",
  },
  solution: {
    label: "Solution",
    icon: Sigma,
    frame: "border-border-strong bg-muted",
    labelColor: "text-secondary-foreground",
  },
  step: {
    label: "Step",
    icon: CornerDownRight,
    frame: "border-l-border-strong border-l-2 border-y-0 border-r-0 rounded-none bg-transparent",
    labelColor: "text-muted-foreground",
  },
  result: {
    label: "Result",
    icon: BadgeCheck,
    frame: "border-success/50 bg-success/5",
    labelColor: "text-success",
  },
  check: {
    label: "Check",
    icon: BadgeCheck,
    frame: "border-border-strong bg-muted",
    labelColor: "text-secondary-foreground",
  },
  pitfall: {
    label: "Common pitfall",
    icon: TriangleAlert,
    frame: "border-warning/50 bg-warning/5",
    labelColor: "text-warning",
  },
  symbols: {
    label: "Symbols",
    icon: Baseline,
    frame: "border-border-strong bg-muted",
    labelColor: "text-secondary-foreground",
  },
  idea: {
    label: "Key idea",
    icon: Lightbulb,
    frame: "border-primary/40 bg-brand-soft",
    labelColor: "text-primary",
  },
};

interface CalloutProps {
  readonly kind: CalloutKind;
  /** Trailing text on the marker line, e.g. `[!step] Choose coordinates`. */
  readonly title?: string | undefined;
  readonly children: ReactNode;
}

export function Callout({ kind, title, children }: CalloutProps) {
  const style = STYLES[kind];
  const Icon = style.icon;

  return (
    <aside className={`my-6 rounded-lg border px-4 py-3 ${style.frame}`}>
      <p
        className={`mb-2 flex items-center gap-2 font-sans text-xs font-semibold tracking-[0.08em] uppercase ${style.labelColor}`}
      >
        <Icon className="size-3.5" aria-hidden="true" />
        {style.label}
        {title && (
          <span className="font-normal tracking-normal normal-case opacity-80">
            — {title}
          </span>
        )}
      </p>
      <div className="[&>:first-child]:mt-0 [&>:last-child]:mb-0">{children}</div>
    </aside>
  );
}

const MARKER = /^\[!([a-zA-Z]+)\][ \t]*(.*)$/;

export interface CalloutMarker {
  readonly kind: CalloutKind;
  readonly title: string | undefined;
  /** The exact marker text, so the renderer can excise it by match. */
  readonly raw: string;
}

/**
 * Read a `[!problem]` marker off the first line of a blockquote.
 *
 * Returns null for an ordinary quotation, which stays a quotation — the tutor
 * uses those for emphasis and they must not become furniture by accident.
 */
export function readMarker(firstLine: string): CalloutMarker | null {
  const match = MARKER.exec(firstLine.trimStart());
  if (!match) return null;

  const kind = match[1]!.toLowerCase();
  if (!(CALLOUT_KINDS as readonly string[]).includes(kind)) return null;

  const title = match[2]?.trim();
  return {
    kind: kind as CalloutKind,
    title: title && title.length > 0 ? title : undefined,
    raw: match[0],
  };
}
